import cx from "classnames"
import htmr from "htmr"
import PropTypes from "prop-types"
import { useState } from "react"
import Button from "components/Button"
import Img from "components/Img"
import ReachOutModal from "components/ReachOutModal"
import parseYoutubeIdFromUrl from "lib/validateYoutubeUrl"
import { useFeatureFlags } from "effects/useSiteContent"
import * as css from "./Event.module.css"
import playIcon from "./play-icon.svg"

export const eventShape = {
  title: PropTypes.string.isRequired,
  date: PropTypes.string.isRequired,
  type: PropTypes.string,
  category: PropTypes.string,
  role: PropTypes.string,
  location: PropTypes.string,
  description: PropTypes.oneOfType([
    PropTypes.string.isRequired,
    PropTypes.object.isRequired,
  ]),
  url: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  thumbnail: PropTypes.object,
  buttons: PropTypes.arrayOf(
    PropTypes.shape({ label: PropTypes.string, url: PropTypes.string })
  ),
}

function Event(props) {
  const {
    title,
    date,
    type,
    category,
    role,
    location,
    description,
    url,
    thumbnail,
    slug,
    buttons,
    isUpcoming,
  } = props

  const isConference = type === "conference"
  const [isReachOutOpen, setIsReachOutOpen] = useState(false)
  const { enableTutorialsRedirect } = useFeatureFlags()
  const youtubeId = url ? parseYoutubeIdFromUrl(url) : null
  const eventPageUrl =
    youtubeId && !isUpcoming && enableTutorialsRedirect
      ? `/tutorials/?watch=${slug}`
      : !url || youtubeId
        ? `/events/${slug}/`
        : url

  const metaParts = [role, category, location].filter(Boolean)

  const renderSingleButtonLabel = () => {
    if (!isUpcoming && youtubeId)
      return (
        <>
          Watch Video{" "}
          <img
            className={css.playIcon}
            src={playIcon}
            alt=""
            aria-hidden="true"
          />
        </>
      )
    if (isUpcoming) return "Register"
    return "Learn More"
  }

  return (
    <div
      className={cx(css.root, {
        [css.isUpcoming]: !!isUpcoming,
        [css.isPast]: !isUpcoming,
      })}
    >
      <div className={css.dot} />
      <time className={css.date}>{date}</time>
      <div className={css.card}>
        <div
          className={cx(css.thumbnailColumn, {
            [css.logoColumn]: isConference,
          })}
        >
          <div
            className={cx(css.thumbnailWrapper, {
              [css.logoWrapper]: isConference,
            })}
          >
            <Img
              image={thumbnail?.gatsbyImageData ? thumbnail : undefined}
              src={thumbnail?.url}
              alt={thumbnail?.alt || ""}
              className={isConference ? css.logo : css.thumbnail}
            />
          </div>
        </div>
        <div className={css.infoColumn}>
          <div className={css.meta}>{metaParts.join(" — ")}</div>
          <h3 className={css.title}>{title}</h3>
          {description && (
            <div className={css.description}>
              {htmr(description.childMarkdownRemark?.html, {
                transform: {
                  a: ({ href, ...subProps }) => (
                    <a href={href} className="text-lush" {...subProps} />
                  ),
                },
              })}
            </div>
          )}
          {isConference && buttons?.length > 0 ? (
            <div className={css.buttonGroup}>
              {buttons.map((btn, i) => (
                <Button
                  key={btn.url}
                  className={css.button}
                  href={btn.url}
                  primary={i === 0}
                >
                  {btn.label}
                </Button>
              ))}
              {isUpcoming && (
                <Button
                  className={css.button}
                  onClick={() => setIsReachOutOpen(true)}
                >
                  Reach Out
                </Button>
              )}
              <ReachOutModal
                isOpen={isReachOutOpen}
                onClose={() => setIsReachOutOpen(false)}
                initialMessage={`Reaching out about ${title}`}
              />
            </div>
          ) : (
            <Button className={css.button} href={eventPageUrl} primary>
              {renderSingleButtonLabel()}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

export default Event
